import { useState } from 'react';
import {
  fixableFieldsFor,
  type CorrectionSemantic,
  type Fact,
  type FixableField,
} from '@ni/shared';
import { useAnswerDecision, useCorrectFact } from '../../api/hooks';
import { standingAnswerLine, standingAnswerOf } from '../../api/errors';
import { ErrorLine } from '../ui/error-line';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';
import { Button } from '../ui/button';
import { ConfirmModal } from '../ui/confirm-modal';
import { WHAT_YOU_KNOW } from '../../lib/copy';

/**
 * Correct this (ui-design §4.7). Each option is a sentence a person would say.
 * Only wrong-person confirms first: it is the one that deletes payload.
 *
 * **Fix it** (2026-09-30) is offered only where the fact's type has fields the
 * office may fix (`FIXABLE_FIELDS`), and sends only the fields that changed.
 *
 * On a **queued** row, wrong-person and keep-off-the-brief are the adjudication's
 * own answers by contract, while the other two go through the correction route,
 * which latches the pending row server-side. One popover either way.
 */

interface Option {
  semantic: CorrectionSemantic;
  label: string;
  sub: string;
}

const FIX_OPTION: Option = { semantic: 'fix', label: 'Fix it', sub: 'Part of this is wrong.' };

const OPTIONS: Option[] = [
  { semantic: 'wrong_person', label: 'Wrong person', sub: 'This belongs to someone else.' },
  { semantic: 'wrong_fact', label: 'Wrong', sub: 'This is not true.' },
  { semantic: 'no_longer_true', label: 'No longer true', sub: 'It was true, and ended.' },
  {
    semantic: 'do_not_surface',
    label: 'Keep off the brief',
    sub: 'True, but it should not appear.',
  },
];

/** What the fact says now, per fixable field, as the form's starting text. */
export function fixStart(fact: Fact, fixable: readonly FixableField[]): Record<string, string> {
  return Object.fromEntries(
    fixable.map(({ field }) => {
      const value = fact.fields[field];
      return [field, typeof value === 'string' ? value : ''];
    }),
  );
}

/**
 * The fields the office changed, trimmed. Emptying a detail the line had sends
 * `null`, which clears it; the server refuses that for a detail the line needs.
 */
export function fixSet(
  start: Record<string, string>,
  edits: Record<string, string>,
): Record<string, string | null> {
  const set: Record<string, string | null> = {};
  for (const [field, raw] of Object.entries(edits)) {
    const value = raw.trim();
    const was = (start[field] ?? '').trim();
    if (value === was) continue;
    set[field] = value.length > 0 ? value : null;
  }
  return set;
}

/** One input per fixable field, filled with what the line says now. */
export function FixFields({
  fixable,
  edits,
  onEdit,
}: {
  fixable: readonly FixableField[];
  edits: Record<string, string>;
  onEdit: (field: string, value: string) => void;
}) {
  return (
    <>
      {fixable.map(({ field, label, kind }) => (
        <div className="field" key={field}>
          <label className="label" htmlFor={`fix-${field}`}>
            {label}
          </label>
          <input
            id={`fix-${field}`}
            type="text"
            value={edits[field] ?? ''}
            placeholder={kind === 'date' ? 'YYYY, YYYY-MM or YYYY-MM-DD' : undefined}
            onChange={(event) => onEdit(field, event.target.value)}
          />
        </div>
      ))}
    </>
  );
}

/** A month picker is precision enough; the contract takes `YYYY-MM-DD`. */
function monthToDate(month: string): string {
  return /^\d{4}-\d{2}$/.test(month) ? `${month}-01` : month;
}

export function CorrectThis({
  subjectId,
  fact,
  queuedDecisionId = null,
  runId = null,
  onClose,
}: {
  subjectId: string;
  fact: Fact;
  queuedDecisionId?: string | null;
  runId?: string | null;
  onClose: () => void;
}) {
  const correct = useCorrectFact(subjectId);
  const answer = useAnswerDecision(subjectId);

  const fixable = fixableFieldsFor(fact.fact_type);
  const options = fixable.length > 0 ? [FIX_OPTION, ...OPTIONS] : OPTIONS;
  const [start] = useState(() => fixStart(fact, fixable));
  const [edits, setEdits] = useState(start);

  const [semantic, setSemantic] = useState<CorrectionSemantic>(
    fixable.length > 0 ? 'fix' : 'wrong_fact',
  );
  const [detail, setDetail] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [confirming, setConfirming] = useState(false);

  const working = correct.isPending || answer.isPending;
  const standing = standingAnswerOf(answer.error);
  const set = fixSet(start, edits);
  const blocked =
    (semantic === 'no_longer_true' && endMonth.length === 0) ||
    (semantic === 'fix' && Object.keys(set).length === 0);

  const perform = () => {
    // The two semantics the queued row can answer directly. Same machinery by
    // contract: leave_out *is* keep-off-the-brief.
    if (queuedDecisionId && semantic === 'wrong_person') {
      answer.mutate(
        { decisionId: queuedDecisionId, body: { wrong_person: true }, runId },
        { onSuccess: onClose },
      );
      return;
    }
    if (queuedDecisionId && semantic === 'do_not_surface') {
      answer.mutate(
        { decisionId: queuedDecisionId, body: { leave_out: true }, runId },
        { onSuccess: onClose },
      );
      return;
    }

    const trimmed = detail.trim();
    const withDetail = trimmed.length > 0 ? { detail: trimmed } : {};
    const body =
      semantic === 'no_longer_true'
        ? { semantic, end_date: monthToDate(endMonth), ...withDetail }
        : semantic === 'fix'
          ? { semantic, set, ...withDetail }
          : { semantic, ...withDetail };

    correct.mutate({ lineageId: fact.lineage_id, body, runId }, { onSuccess: onClose });
  };

  const submit = () => {
    if (working || blocked) return;
    if (semantic === 'wrong_person' && !confirming) {
      setConfirming(true);
      return;
    }
    perform();
  };

  if (confirming) {
    return (
      <ConfirmModal
        title="Wrong person"
        line="Removes it. Later research will not add it back for this person."
        confirmLabel="Remove it"
        workingLabel="Removing…"
        working={working}
        onConfirm={perform}
        dismissLabel="Cancel"
        onClose={() => setConfirming(false)}
      >
        {correct.isError ? <ErrorLine error={correct.error} /> : null}
        {standing ? <div className="src">{standingAnswerLine(standing)}</div> : null}
        {answer.isError && !standing ? <ErrorLine error={answer.error} /> : null}
      </ConfirmModal>
    );
  }

  return (
    <Modal label="Correct this" narrow onClose={onClose}>
      <div className="modal-head">Correct this</div>

      {options.map((option) => (
        <label className="option-row" key={option.semantic}>
          <input
            type="radio"
            name="correct-semantic"
            checked={semantic === option.semantic}
            onChange={() => setSemantic(option.semantic)}
          />
          <span>
            <span className="opt-label">{option.label}</span>
            <span className="opt-sub">{option.sub}</span>
          </span>
        </label>
      ))}

      {semantic === 'fix' ? (
        <FixFields
          fixable={fixable}
          edits={edits}
          onEdit={(field, value) => setEdits((prior) => ({ ...prior, [field]: value }))}
        />
      ) : null}

      {semantic === 'no_longer_true' ? (
        <div className="field last">
          <label className="label" htmlFor="correct-end">
            When did it end?
          </label>
          <input
            id="correct-end"
            type="month"
            value={endMonth}
            onChange={(event) => setEndMonth(event.target.value)}
          />
        </div>
      ) : (
        <div className="field last">
          <label className="label" htmlFor="correct-detail">
            {WHAT_YOU_KNOW}
          </label>
          <input
            id="correct-detail"
            type="text"
            value={detail}
            onChange={(event) => setDetail(event.target.value)}
          />
        </div>
      )}

      {correct.isError ? <ErrorLine error={correct.error} /> : null}
      {standing ? <div className="src">{standingAnswerLine(standing)}</div> : null}
      {answer.isError && !standing ? <ErrorLine error={answer.error} /> : null}

      <div className="modal-actions">
        <WorkingButton
          label="Save"
          workingLabel="Saving…"
          working={working}
          disabled={blocked}
          onClick={submit}
        />
        <Button variant="act" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
